import { defineStore } from 'pinia'
import { ComputedRef, computed, ref } from 'vue'

import { useStorage } from '@vueuse/core'

import { useSiteCreateMutation } from '../sites/composables/useSite'

import { Site } from '../sites/models/site'
import { SiteConfig } from '../sites/models/siteConfig'
import { SitePermission } from '../sites/models/sitePermission'
import { SiteTerms } from '../sites/models/siteTerms'

import {
  show,
  showConfig,
  showUserPermissions,
} from '../sites/siteService'
import { fetchSites } from './authSiteService'
import { useFeatureFlagStore } from '../features/featureFlagStore'

export interface SiteContext {
  site: Site
  config: SiteConfig
  permissions: SitePermission[]
}

const LAST_SITE_SLUG_KEY = 'last_site_slug'

export const useAuthSiteStore = defineStore('AuthSiteStore', () => {
  const featureFlagStore = useFeatureFlagStore()

  const isFetchingSites = ref(false)
  const sitesError = ref()
  const sites = ref<Site[] | null>(null)

  const currentSite = ref<Site | null>(null)
  const config = ref<SiteConfig | null>(null)
  const permissions = ref<SitePermission[]>([])

  let sitesPromise: Promise<Site[]> | null = null
  let contextPromise: Promise<void> | null = null

  const ensureSites = async () => {
    if (sites.value) return sites.value

    if (!sitesPromise) {
      sitesPromise = fetchSites({ name: '' }, 15, 1)
        .then((res) => {
          sites.value = res.sites
          return res.sites
        })
        .finally(() => {
          sitesPromise = null
        })
    }
    return sitesPromise
  }

  const refreshSites = async () => {
    sites.value = null
    sitesPromise = null

    return ensureSites()
  }

  const findBySlug = (slug: string) => {
    return sites.value?.find((s) => s.slug === slug) ?? null
  }

  const ensureSiteContext = async (site: Site) => {
    if (
      currentSite.value?.slug === site.slug &&
      config.value &&
      permissions.value
    ) {
      return
    }
    if (currentSite.value?.slug !== site.slug) {
      config.value = null
      permissions.value = []
      contextPromise = null
    }
    currentSite.value = site
    clearContext()

    if (!contextPromise) {
      const promise: Promise<void> = Promise.all([
        show(site.id),
        showConfig(site.id),
        showUserPermissions(site.id),
      ])
        .then(([s, cfg, perms]) => {
          // The user switched site while this was loading; don't overwrite the newer context
          if (currentSite.value?.slug !== site.slug) return

          currentSite.value = s.site
          config.value = cfg.siteConfig
          permissions.value = perms.sitePermissions
          setContext({ site: s.site, config: cfg.siteConfig, permissions: perms.sitePermissions })

          featureFlagStore.hydrate(cfg.siteConfig.featureFlags)
        })
        .finally(() => {
          if (contextPromise === promise) contextPromise = null
        })
      contextPromise = promise
    }
    return contextPromise
  }

  const {
    mutateAsync,
    isPending,
    error: createSiteError,
  } = useSiteCreateMutation()

  const createSite = async ({
    name,
    timezone,
  }: {
    name: string
    timezone: string
  }) => {
    if (isPending.value) return

    const result = await mutateAsync({
      form: { name, timezone },
    })

    await refreshSites()

    return result.site
  }

  //
  const loadedSites = ref<Site[]>([])
  const setLoadedSites = (sites: Site[]) => {
    loadedSites.value = sites
  }
  const clearLoadedSites = () => {
    loadedSites.value = []
  }

  const currentContext = ref<SiteContext | null>(null)
  const isLoaded = computed(() => !!currentContext.value)

  // The router resolves :org by slug (E2E-06)
  const currentSlug = computed(() => currentContext.value?.site.slug)
  const lastSlug = useStorage<string | undefined>(LAST_SITE_SLUG_KEY, undefined)

  const setContext = (context: SiteContext) => {
    currentContext.value = context
    lastSlug.value = context.site.name
  }
  const clearContext = () => {
    currentContext.value = null
    lastSlug.value = undefined
  }

  /** Forget everything loaded for the signed-in user, so the next user starts clean */
  const reset = () => {
    sites.value = null
    sitesPromise = null
    currentSite.value = null
    config.value = null
    permissions.value = []
    contextPromise = null
    currentContext.value = null
    featureFlagStore.reset()
  }

  const settings = computed(() => currentContext.value?.config.settings)
  const features = computed(() => currentContext.value?.config.features)

  const terms: ComputedRef<SiteTerms | null> = computed(
    () => currentContext.value?.config?.terms ?? null,
  )

  const current = computed(() => currentContext.value?.site)

  return {
    isFetchingSites,
    sitesError,
    sites,
    currentSite,
    config,
    permissions,
    ensureSites,
    refreshSites,
    ensureSiteContext,
    findBySlug,

    createSiteError,
    createSite,

    loadedSites,
    setLoadedSites,
    clearLoadedSites,

    currentContext,
    isLoaded,
    currentSlug,
    lastSlug,
    setContext,
    clearContext,
    reset,

    settings,
    features,
    terms,

    current,
  }
})
